/**
 * useDocumentosDaConta — CPF/CNPJ da conta e dados de cobrança nas Configurações (shell).
 * Plano: docs/PLANO-DOCUMENTO-CPF-CNPJ-E-COBRANCA.md (TODO 3.6). Contrato: shell/settings.vm.js @v3
 * (`documents` + `editDocuments` + as ações *EditDocuments).
 *
 * Ciclo:
 *  - carregar(): GET /users/documents (o Layout chama ao abrir as Configurações).
 *  - editar um bloco (conta OU cobrança) → valida no front (dígito verificador; vazio = limpar) →
 *    PUT /users/documents com os TRÊS valores (o PUT grava exatamente o que vem, então o bloco que
 *    não está sendo editado vai com o valor atual) → guarda a resposta.
 *  - Sem os dados carregados a edição fica bloqueada: salvar sem eles apagaria o outro bloco.
 */
import { useState, useCallback } from 'react';
import usersService from '../services/usersService';
import { mascararDocumento, validarDocumento } from '../utils/documento';

const EDICAO_FECHADA = {
  open: false, kind: 'account', title: '', helper: '', showName: false,
  name: '', nameError: null, document: '', documentError: null, formError: null, saving: false,
};

const TEXTOS_EDICAO = {
  account: {
    title: 'CPF ou CNPJ da conta',
    helper: 'Identifica a conta. Se a cobrança estiver em branco, é ele que vai no pagamento.',
    showName: false,
  },
  billing: {
    title: 'Dados de cobrança',
    helper: 'Vão no comprovante do pagamento. Em branco, usamos o nome e o documento da conta.',
    showName: true,
  },
};

export default function useDocumentosDaConta() {
  const [dados, setDados] = useState(null);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState(null);
  const [edicao, setEdicao] = useState(EDICAO_FECHADA);

  const carregar = useCallback(async () => {
    setLoading(true);
    setErro(null);
    try {
      setDados(await usersService.getDocuments());
    } catch (e) {
      console.warn('[Settings] Não foi possível carregar os documentos:', e);
      setErro('Não foi possível carregar seus documentos.');
    } finally {
      setLoading(false);
    }
  }, []);

  const abrirEdicao = useCallback((kind) => {
    if (!dados) return;
    const ehConta = kind === 'account';
    setEdicao({
      ...EDICAO_FECHADA,
      ...TEXTOS_EDICAO[kind],
      open: true,
      kind,
      document: mascararDocumento((ehConta ? dados.document : dados.billingDocument) ?? ''),
      name: ehConta ? '' : (dados.billingName ?? ''),
    });
  }, [dados]);

  const onChangeEditDocument = useCallback((valor) => {
    setEdicao((e) => ({ ...e, document: mascararDocumento(valor), documentError: null, formError: null }));
  }, []);

  const onChangeEditDocumentsName = useCallback((valor) => {
    setEdicao((e) => ({ ...e, name: valor, nameError: null, formError: null }));
  }, []);

  const onCancelEditDocuments = useCallback(() => {
    setEdicao((e) => (e.saving ? e : EDICAO_FECHADA));
  }, []);

  const onSaveEditDocuments = useCallback(async () => {
    if (!dados || edicao.saving) return;

    // Vazio é permitido: limpa o campo. Preenchido tem de passar no dígito verificador.
    let documento = null;
    if (edicao.document.trim()) {
      const v = validarDocumento(edicao.document);
      if (!v.valido) { setEdicao((e) => ({ ...e, documentError: v.erro })); return; }
      documento = v.limpo;
    }
    const nome = edicao.name.trim();
    if (nome.length > 255) {
      setEdicao((e) => ({ ...e, nameError: 'Nome muito longo (máximo 255 caracteres).' }));
      return;
    }

    const corpo = edicao.kind === 'account'
      ? { document: documento, billingDocument: dados.billingDocument, billingName: dados.billingName }
      : { document: dados.document, billingDocument: documento, billingName: nome || null };

    setEdicao((e) => ({ ...e, saving: true, formError: null }));
    try {
      setDados(await usersService.updateDocuments(corpo));
      setEdicao(EDICAO_FECHADA);
    } catch (e) {
      setEdicao((atual) => ({
        ...atual,
        saving: false,
        formError: e?.response?.data?.message || 'Não foi possível salvar. Tente novamente.',
      }));
    }
  }, [dados, edicao]);

  const documents = (() => {
    if (loading && !dados) {
      return { loading: true, error: null, canEdit: false, accountDocument: 'Carregando…', billingName: 'Carregando…', billingDocument: 'Carregando…' };
    }
    if (!dados) {
      return { loading: false, error: erro, canEdit: false, accountDocument: '—', billingName: '—', billingDocument: '—' };
    }
    const contaTemDocumento = !!dados.document;
    return {
      loading: false,
      error: null,
      canEdit: true,
      accountDocument: contaTemDocumento ? mascararDocumento(dados.document) : 'Não informado',
      billingName: dados.billingName || 'Igual ao nome da conta',
      billingDocument: dados.billingDocument
        ? mascararDocumento(dados.billingDocument)
        : (contaTemDocumento ? 'Igual ao da conta' : 'Não informado'),
    };
  })();

  return {
    carregar,
    documents,
    editDocuments: edicao,
    actions: {
      onOpenEditAccountDocument: () => abrirEdicao('account'),
      onOpenEditBilling: () => abrirEdicao('billing'),
      onChangeEditDocument,
      onChangeEditDocumentsName,
      onSaveEditDocuments,
      onCancelEditDocuments,
    },
  };
}
